import {MapChoroplethDatum, MapChoroplethKind} from './types'

/**
 * ── THE CHOROPLETH'S COLOUR SCALE (VERA-260) ────────────────────────────────────────
 *
 * Pure functions: no React, no deck.gl, no Google. The map's rendering effect and its
 * legend both call in here, which is the point — a legend derived independently of the
 * fill is a legend that will eventually describe a scale the map is not using, and a
 * choropleth whose legend is wrong is worse than one with no legend, because it is
 * confidently wrong.
 */

/**
 * The sequential ramp, low → high, as RGB triples.
 *
 * Brand purple, the SAME family as `HEATMAP_COLOR_RANGE` so the two map modes read as one
 * product rather than two visualisations that happen to share a card.
 *
 * ⚠️ NOT RED AT THE HOT END, and this is the constraint that actually rules out every
 * off-the-shelf sequential ramp. On this map red already means INVALID/TAMPERED — it is
 * the pin colour for a scan that failed verification. A ramp topping out in red would
 * therefore paint a brand's busiest country in the exact colour that, four feet away in
 * pins mode, means something is wrong. The two readings are not merely different, they
 * are opposite, and nothing on screen tells you which one you are looking at. (Which is
 * doubly perverse here, because one of the metrics this mode exists to show IS the
 * invalid rate — so the "everything is fine, there is just a lot of it" country and the
 * "a third of scans here are counterfeit" country would render identically.)
 *
 * Ascending in LIGHTNESS, not saturation, because the basemap is near-black: on a dark
 * surface the eye reads brightness as magnitude, so the top of the scale has to be the
 * lightest stop. Single-hue for the usual reason — hue is read as categorical, so a
 * rainbow ramp invents boundaries the data does not have. That single-hue is also the
 * accessible choice (it varies on the lightness axis rather than the red/green one ~8% of
 * men cannot separate) is a bonus, not the reason.
 *
 * ⚠️ The DARKEST stop is brand.900, not the heat ramp's brand.950. Heat can go that dark
 * because its lowest band is semi-transparent and mostly culled; here the bottom band is
 * a solid fill on a #17181a basemap, and #341967 against that is dim enough that "lowest
 * band" starts to read as "unpainted" — which is the one confusion this mode cannot
 * afford, since unpainted means "no scans" and the bottom band means "scans, lowest
 * value". The per-polygon stroke below is the second, independent guard on the same
 * confusion.
 */
export const CHOROPLETH_COLOR_RANGE: readonly (readonly [
  number,
  number,
  number,
])[] = [
  [74, 35, 136], //   brand.900 — the floor, lifted off the basemap
  [93, 45, 170], //   brand.800
  [111, 54, 203], //  brand.700
  [127, 72, 223], //  brand.600 — the brand's own stop
  [160, 128, 238], // between brand.500 and brand.300
  [199, 187, 247], // brand.300 — the top
]

/** Fill alpha for a painted country. Below 1 so the basemap's country labels stay
 *  readable through it — a coloured shape nobody can name is not much use. */
export const CHOROPLETH_FILL_ALPHA = 200

/**
 * "Too few scans to report a rate" — the THIRD state.
 *
 * A neutral grey, deliberately outside the purple ramp: it must not read as a position on
 * the scale (which is the entire failure a threshold exists to prevent), and it must not
 * read as unpainted either. Grey is the conventional cartographic answer for "measured
 * but not reportable" and is the only hue on this map that carries no other meaning.
 */
export const CHOROPLETH_INSUFFICIENT_COLOR: readonly [number, number, number] = [
  106, 106, 114,
]
export const CHOROPLETH_INSUFFICIENT_ALPHA = 150

/**
 * The outline on every painted country.
 *
 * Not decoration. It is what makes "painted at the bottom of the scale" and "not painted
 * at all" distinguishable at a glance, independently of the fill colour — so the
 * distinction survives a future retune of the ramp, a satellite basemap, or a display
 * that crushes shadows.
 */
export const CHOROPLETH_STROKE_COLOR: readonly [number, number, number] = [
  199, 187, 247,
]
export const CHOROPLETH_STROKE_ALPHA = 70

/** How many bands the legend and the fill both use. One per ramp stop, by construction. */
export const CHOROPLETH_BANDS = CHOROPLETH_COLOR_RANGE.length

/** One legend row: a swatch and the value range it covers. */
export interface ChoroplethBand {
  /** Inclusive lower bound. */
  from: number
  /** Exclusive upper bound, except on the last band where it is inclusive. */
  to: number
  color: readonly [number, number, number]
}

export interface ChoroplethScale {
  bands: ChoroplethBand[]
  /** Highest value in the coloured set. `0` when nothing is colourable. */
  max: number
  /** Rows suppressed by the minimum-denominator rule. Drives the legend's third swatch:
   *  it appears only when it describes something actually on screen. */
  insufficientCount: number
  /** Rows that cleared the threshold and therefore have a colour. */
  coloredCount: number
}

/** Does this row clear the small-sample floor? Rates only — see `MapChoroplethConfig`. */
export const isInsufficient = (
  d: MapChoroplethDatum,
  kind: MapChoroplethKind,
  minDenominator?: number,
): boolean =>
  kind === 'rate' &&
  minDenominator !== undefined &&
  minDenominator > 0 &&
  d.denominator < minDenominator

/**
 * The scale, from the data that will actually be painted.
 *
 * ## Linear over `[0, max]`, and the alternative that was rejected
 *
 * Equal-width bands from zero to the largest coloured value. The competing standard is
 * QUANTILE classification (equal COUNT per band), which is better at spreading a skewed
 * distribution — and that is exactly why it is wrong here. Quantile encodes RANK, not
 * magnitude: the top band means "in the top sixth of countries", which is true whether
 * that country's invalid rate is 4% or 40%, and a reader has no way to tell from the
 * colour. On a surface whose whole claim is that its figures are reportable, a colour
 * that means "relatively high" while looking like it means "high" is the wrong trade.
 *
 * Zero anchors the low end rather than the minimum observed value, for the same reason:
 * with a `[min, max]` scale, the country with the lowest invalid rate is painted at the
 * bottom of the ramp whether that rate is 0.1% or 12%, so a set of uniformly-bad
 * countries would render as a healthy-looking gradient. Anchoring at 0 keeps the bottom
 * band meaning "near zero" always.
 *
 * ⚠️ The known cost, stated rather than hidden: a heavily skewed distribution compresses
 * into the bottom band or two, so real differences among the smaller countries become
 * invisible. The legend's printed band boundaries are what makes that legible rather than
 * misleading — a reader can see that five countries share the "0 – 216" band and that the
 * band is wide. If real data turns out to be skewed enough that this loses too much,
 * quantile classification is the change to make, and the legend already has somewhere to
 * say so.
 */
export const buildChoroplethScale = (
  data: MapChoroplethDatum[],
  kind: MapChoroplethKind,
  minDenominator?: number,
): ChoroplethScale => {
  const colorable = data.filter((d) => !isInsufficient(d, kind, minDenominator))
  const insufficientCount = data.length - colorable.length

  const values = colorable
    .map((d) => d.value)
    .filter((v) => typeof v === 'number' && Number.isFinite(v))

  const rawMax = values.length ? Math.max(...values) : 0
  /*
    A max of 0 (every country at a 0% rate — a real and GOOD outcome) would make every
    band `[0, 0]` and every lookup land in band 0. That renders correctly by accident, but
    the legend would print six identical rows. Widen to a nominal 1 so the legend reads as
    a single meaningful "0" band instead of six copies of it.
  */
  const max = rawMax > 0 ? rawMax : kind === 'rate' ? 1 : 1

  const step = max / CHOROPLETH_BANDS
  const bands: ChoroplethBand[] = CHOROPLETH_COLOR_RANGE.map((color, i) => ({
    from: step * i,
    to: i === CHOROPLETH_BANDS - 1 ? max : step * (i + 1),
    color,
  }))

  return {bands, max, insufficientCount, coloredCount: colorable.length}
}

/**
 * A value → its band index.
 *
 * Clamped at both ends: floating-point division puts `value === max` at exactly
 * `CHOROPLETH_BANDS`, one past the last band, and an unclamped lookup would return
 * `undefined` for the single most important country on the map.
 */
export const bandIndexFor = (value: number, scale: ChoroplethScale): number => {
  if (!Number.isFinite(value) || scale.max <= 0) return 0
  const raw = Math.floor((value / scale.max) * CHOROPLETH_BANDS)
  return Math.max(0, Math.min(CHOROPLETH_BANDS - 1, raw))
}

/** `[r,g,b]` for a datum, or the neutral grey when the threshold suppressed it. */
export const colorFor = (
  d: MapChoroplethDatum,
  kind: MapChoroplethKind,
  scale: ChoroplethScale,
  minDenominator?: number,
): readonly [number, number, number] =>
  isInsufficient(d, kind, minDenominator)
    ? CHOROPLETH_INSUFFICIENT_COLOR
    : CHOROPLETH_COLOR_RANGE[bandIndexFor(d.value, scale)]

/** `rgba(...)` for the legend's DOM swatches — the same triples the WebGL layer uses. */
export const rgba = (
  color: readonly [number, number, number],
  alpha: number,
): string => `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${(alpha / 255).toFixed(3)})`

/**
 * A value as the reader should see it.
 *
 * Rates get one decimal place, which is the resolution the underlying counts can actually
 * support at realistic volumes — printing "3.87%" off a denominator of a few thousand
 * claims a precision the sample does not have. Counts get thousands separators and no
 * decimals, because a fractional scan does not exist.
 */
export const formatChoroplethValue = (
  value: number,
  kind: MapChoroplethKind,
): string =>
  kind === 'rate'
    ? `${(value * 100).toFixed(1)}%`
    : Math.round(value).toLocaleString()

/**
 * The fraction, spelled out — "248 of 6,420".
 *
 * ⚠️ ALWAYS rendered beside the value in the tooltip, never conditionally. A percentage
 * with no denominator is not a reportable figure, and it is specifically the thing that
 * makes a 50%-of-two country indistinguishable from a 50%-of-ten-thousand one.
 */
export const formatChoroplethFraction = (d: MapChoroplethDatum): string =>
  `${d.numerator.toLocaleString()} of ${d.denominator.toLocaleString()}`
